'use client';

import { useEffect, useState } from 'react';
import { IconButton } from 'subframe/components/IconButton';
import { TextArea } from 'subframe/components/TextArea';
import { Button } from 'subframe/components/Button';
import { Dialog } from 'subframe/components/Dialog';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSnackbar } from 'notistack';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { updateUpgradePlan } from 'api/frontend';
import { AtomicTooltip } from 'components/design-system';
import FormProvider from 'components/FormProvider';
import * as Sentry from '@sentry/react';
import {
  charLimitExceededErrorMessage,
  charLimits,
} from 'constants/input-validation';
import { somethingWentWrong } from 'constants/toasts';

interface MarkUpgradeAsArchivedProps {
  upgradeId: string;
  mutateUpgrade: () => void;
  open?: boolean;
  onClose?: () => void;
  isFromLandingPage?: boolean;
}

export default function MarkUpgradeAsArchived({
  upgradeId,
  mutateUpgrade,
  open,
  onClose,
  isFromLandingPage,
}: MarkUpgradeAsArchivedProps) {
  const { account, currentOrganization } = useUserAccountState();
  const { enqueueSnackbar } = useSnackbar();
  const { logEvent } = AnalyticsEventLogger();
  const [openDialog, setOpenDialog] = useState(open);

  const markAsArchivedSchema = Yup.object().shape({
    comment: Yup.string()
      .optional()
      .max(
        charLimits.ActionDetails,
        charLimitExceededErrorMessage('Comments', charLimits.ActionDetails),
      ),
  });

  const defaultValues: { comment: string } = {
    comment: '',
  };

  const methods = useForm<{ comment: string }>({
    resolver: yupResolver(markAsArchivedSchema),
    defaultValues: defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;

  const resetToDefault = () => {
    setValue('comment', '');
  };

  const onSubmit = async (data: { comment: string }) => {
    try {
      const response = await updateUpgradePlan(
        upgradeId,
        { status: 'archived', status_comment: data.comment },
        {
          headers: {
            Authorization: `Bearer ${account.token}`,
          },
        },
      );

      if (response?.__status === 200) {
        enqueueSnackbar('Upgrade Plan marked as Archived', {
          variant: 'success',
        });
        logEvent('upgrade_plan_marked_as_archived', {
          upgrade_id: upgradeId,
          account_id: account.accountId,
          organization_id: currentOrganization.id,
        });
        mutateUpgrade();
      } else {
        enqueueSnackbar(
          somethingWentWrong.replace(
            '<action>',
            'marking Upgrade Plan as Archived',
          ),
          {
            variant: 'error',
          },
        );
        Sentry.captureException(
          somethingWentWrong.replace(
            '<action>',
            'marking Upgrade Plan as Archived',
          ),
          {
            extra: { response, upgradeId, data },
          },
        );
      }
    } catch (e) {
      Sentry.captureException(e);
    }

    setOpenDialog(false);
    if (onClose) {
      onClose();
    }
    resetToDefault();
  };

  if (open === false && openDialog === true) {
    setOpenDialog(false);
    if (onClose) {
      onClose();
    }
  }

  useEffect(() => {
    setOpenDialog(open);
  }, [open]);

  return (
    <div style={{ display: 'contents' }}>
      {isFromLandingPage && (
        <AtomicTooltip tooltipContent={`Mark the Upgrade Plan as Archived.`}>
          <IconButton
            icon="FeatherCheck"
            size="medium"
            onClick={() => setOpenDialog(true)}
          />
        </AtomicTooltip>
      )}
      <Dialog
        open={openDialog}
        onOpenChange={(openStatus) => {
          setOpenDialog(openStatus);
          if (onClose) {
            onClose();
          }
          resetToDefault();
        }}
      >
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Content className="h-auto w-auto flex-none">
            <div className="flex w-full min-w-[384px] max-w-[448px] flex-col items-start gap-6 pt-6 pr-6 pb-6 pl-6">
              <div className="flex w-full items-start justify-between">
                <span className="text-subheader font-subheader text-default-font">
                  Ready to mark this Upgrade Plan as Archived?
                </span>
                <IconButton
                  size="medium"
                  icon="FeatherX"
                  onClick={() => {
                    setOpenDialog(false);
                    if (onClose) {
                      onClose();
                    }
                  }}
                />
              </div>
              <span className="text-body font-body text-default-font">
                The Upgrade Plan will become read-only once marked as Archived.
              </span>
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Comments (Optional)
                </span>
                <TextArea
                  className="h-auto w-full flex-none"
                  helpText={errors.comment?.message}
                  error={methods.getFieldState('comment').invalid}
                >
                  <TextArea.Input
                    className="h-auto min-h-[96px] w-full flex-none"
                    placeholder=""
                    {...methods.register('comment')}
                  />
                </TextArea>
              </div>
              <div className="flex w-full items-end justify-end gap-4">
                <Button
                  variant="brand-primary"
                  size="large"
                  icon="FeatherArchive"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  Archive
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </FormProvider>
      </Dialog>
    </div>
  );
}
